import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Wifi, Phone, MapPin, Copy, Instagram, Facebook, X, Star, QrCode, Mail, Globe } from "lucide-react";
import { T, tr } from "../lib/i18n";
import { LanguageSelector } from "./LanguageSelector";
import { MenuPill } from "./MenuPill";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { toast } from "../hooks/use-toast";
import { RoomShortcut } from "./RoomShortcut";

const WIFI_ALL = {
  it: "Password valida per tutte le Wi-Fi dei Giganti",
  en: "Password valid for all Giganti Wi-Fi networks",
  es: "Contraseña válida para todas las redes Wi-Fi de los Giganti",
  fr: "Mot de passe valable pour tous les Wi-Fi des Giganti",
  de: "Passwort gültig für alle Giganti-WLAN-Netze",
};

/*
 * Header: brand banner with looping cover video (hero-cover.mp4) + logo + wordmark.
 */
export function RestaurantHeader({ restaurant, lang, setLang, onOpenQR, primaryAction, leftAction, rightAction, extraRightAction, secondaryAction, tertiaryAction, compact = false, hideCover = false, headerRightSlot = null, stickyBar = false, collapseOnScroll = false }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const { pathname } = useLocation();
  const showHome = compact && pathname !== "/";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: tr(T.copied, lang) });
  };

  return (
    <div className={compact || collapseOnScroll ? "contents" : "relative"}>
      {/* Brand banner (hidden when compact=true → replaced by a slim utility bar) */}
      {compact ? (
        <div className={`${stickyBar ? "sticky top-0 z-[9999] shadow-md" : ""} w-full bg-[#4a1c17] px-3 py-2 flex items-center gap-3`}>
          <div
            aria-label="Il Pozzo dei Giganti"
            className="h-10 w-10 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm shrink-0"
          >
            {restaurant?.logo ? (
              <img src={restaurant.logo} alt="Il Pozzo dei Giganti" className="h-full w-full object-contain p-1" />
            ) : (
              <span className="font-serif font-bold text-[#4a1c17] text-sm">P</span>
            )}
          </div>
          <RoomShortcut />
          <div className="flex-1" />
          {headerRightSlot}
          {showHome && <MenuPill lang={lang} />}
          <LanguageSelector lang={lang} setLang={setLang} onDark />
        </div>
      ) : (
      <>
      {/* SVG defs holding the arched clip-path applied to the sticky cover.
          Using objectBoundingBox so the arch scales with the container. */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
        <defs>
          <clipPath id="cover-arch-clip" clipPathUnits="objectBoundingBox">
            {/* Concave-up ("smile") bottom edge — the cover reaches full
                height on the sides and lifts up in the middle, so the
                menu below peeks through the curve in a welcoming way. */}
            <path d="M 0,0 H 1 V 1 C 0.75,0.88 0.25,0.88 0,1 Z" />
          </clipPath>
        </defs>
      </svg>
      <div
        className={`${collapseOnScroll ? "sticky top-0 z-[9999]" : "relative"} w-full overflow-hidden bg-[#4a1c17]`}
        style={collapseOnScroll ? { clipPath: "url(#cover-arch-clip)", WebkitClipPath: "url(#cover-arch-clip)" } : undefined}
      >
        {/* Cover video background — restaurant clip on loop, muted for autoplay,
            with a burgundy scrim to keep the utility bar + wordmark legible. */}
        {!hideCover && (
          <>
            <video
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              src="/media/hero-cover.mp4"
              poster="/media/hero-cover-poster.jpg"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(180deg, rgba(74,28,23,0.85) 0%, rgba(74,28,23,0.55) 55%, rgba(74,28,23,0.80) 100%)" }}
            />
          </>
        )}
        {/* Utility bar: logo (extreme left) + language (extreme right).
            The QR code button is intentionally omitted here — customers
            already have a persistent QR shortcut in the bottom-right FAB.
            The logo is decorative only; the official website link lives
            inside the Info popup to avoid accidental app-exit taps. */}
        <div className="w-full px-3 py-2 flex items-center gap-3 relative z-20">
          <div
            aria-label="Il Pozzo dei Giganti"
            className="h-10 w-10 rounded-full bg-white flex items-center justify-center overflow-hidden shadow-sm shrink-0"
          >
            {restaurant?.logo ? (
              <img src={restaurant.logo} alt="Il Pozzo dei Giganti" className="h-full w-full object-contain p-1" />
            ) : (
              <span className="font-serif font-bold text-[#4a1c17] text-sm">P</span>
            )}
          </div>
          <RoomShortcut />
          <div className="flex-1" />
          <LanguageSelector lang={lang} setLang={setLang} onDark />
        </div>

        {!hideCover && (
          <div className="relative z-10 max-w-2xl mx-auto px-4 pt-2 pb-10 sm:pt-3 sm:pb-12 text-center">
            <h1 className="font-serif text-[clamp(1.8rem,7.5vw,4rem)] font-semibold tracking-wide uppercase text-[#e6c46a] leading-[1.05] whitespace-nowrap">
              {restaurant?.name || "Il Pozzo dei Giganti"}
            </h1>
            <div className="mt-2 font-serif italic text-xl sm:text-2xl text-[#e6c46a]/85">Menù</div>
          </div>
        )}

        {/* Bottom curve — kept only for the non-collapse case (interior
            pages / menu detail). When `collapseOnScroll` is on the whole
            cover is clip-path'd so this decorative fill is redundant and
            would leave a straight beige band under the arch. */}
        {!hideCover && !collapseOnScroll && (
          <svg className="absolute bottom-[-1px] left-0 right-0 w-full" viewBox="0 0 1200 40" preserveAspectRatio="none" aria-hidden>
            <path d="M0,40 C300,10 900,10 1200,40 L1200,40 L0,40 Z" fill="#f7ebdf" />
          </svg>
        )}
      </div>
      </>
      )}

      {/* Info + Dintorni pill row (informative pages) — hidden in compact mode
          because SubPageNav renders its own Info+Dintorni row */}
      {!compact && (
      <div className="relative max-w-3xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setInfoOpen(true)}
            data-testid="header-info-btn"
            className="flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#faf3ee] border border-[#e8dcc8] py-2.5 px-4 transition-colors group shadow-sm"
          >
            <MapPin className="h-4 w-4 text-[#4a1c17]" />
            <span className="font-serif text-sm font-semibold text-[#2b1410]">{tr(T.info, lang)}</span>
          </button>
          {extraRightAction && (
            <button
              type="button"
              onClick={extraRightAction.onClick}
              data-testid={extraRightAction.testId}
              className="flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#faf3ee] border border-[#e8dcc8] py-2.5 px-4 transition-colors group shadow-sm"
              title={extraRightAction.title || extraRightAction.label}
            >
              <span className="text-[#4a1c17]">{extraRightAction.icon}</span>
              <span className="font-serif text-sm font-semibold text-[#2b1410]">{extraRightAction.label}</span>
            </button>
          )}
        </div>

        {/* Booking services row */}
        {(() => {
          const actions = [primaryAction, leftAction, rightAction, secondaryAction, tertiaryAction].filter(Boolean);
          if (!actions.length) return null;
          const cols = ["", "grid-cols-1", "grid-cols-2", "grid-cols-3", "grid-cols-4", "grid-cols-5"][actions.length] || "grid-cols-5";
          return (
            <div className={`grid gap-1.5 sm:gap-2 mt-2 ${cols}`} data-testid="header-actions">
              {actions.map((a) => (
                <button
                  key={a.testId || a.label}
                  type="button"
                  onClick={a.onClick}
                  data-testid={a.testId}
                  className="flex flex-col items-center justify-center gap-1 rounded-2xl border px-1 py-3 sm:py-3.5 transition-colors group shadow-sm text-center bg-white hover:bg-[#faf3ee] border-[#e8dcc8]"
                  title={a.title || a.label}
                >
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform ${a.highlight ? "bg-[#C48B31] text-white" : "bg-[#4a1c17] text-[#f7ebdf]"}`}>
                    {a.icon}
                  </div>
                  <div className="font-serif text-[11px] sm:text-sm font-semibold leading-tight text-[#2b1410]">{a.label}</div>
                </button>
              ))}
            </div>
          );
        })()}
      </div>
      )}

      {/* Info dialog */}
      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden border-[#e8dcc8]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8dcc8] bg-[#4a1c17]">
            <DialogTitle className="font-serif text-lg font-semibold text-[#f7ebdf] tracking-wide">{restaurant?.name}</DialogTitle>
            <button onClick={() => setInfoOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/10 text-[#f7ebdf] flex items-center justify-center">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <InfoRow icon={<Wifi className="h-5 w-5" />} label={tr(T.wifi, lang)}>
              <div className="text-sm text-[#2b1410]">
                <div className="text-[#7c5a4e]">{WIFI_ALL[lang] || WIFI_ALL.it}</div>
                <button
                  onClick={() => copy(restaurant?.info?.wifi?.password || "")}
                  className="mt-1 inline-flex items-center gap-1.5 text-[#4a1c17] hover:text-[#2b1410] font-semibold text-base"
                  data-testid="info-wifi-password"
                >
                  <span>{restaurant?.info?.wifi?.password}</span>
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </InfoRow>
            <InfoRow icon={<Phone className="h-5 w-5" />} label={tr(T.phone, lang)}>
              <a href={`tel:${restaurant?.info?.phone}`} className="text-sm text-[#4a1c17] font-medium hover:underline">
                {restaurant?.info?.phone}
              </a>
            </InfoRow>
            <InfoRow icon={<Mail className="h-5 w-5" />} label="Email">
              <a href={`mailto:${restaurant?.info?.email}`} className="text-sm text-[#4a1c17] font-medium hover:underline break-all">
                {restaurant?.info?.email}
              </a>
            </InfoRow>
            <InfoRow icon={<MapPin className="h-5 w-5" />} label={tr(T.address, lang)}>
              <div className="text-sm text-[#2b1410]">{restaurant?.info?.address}</div>
            </InfoRow>
            <InfoRow icon={<Globe className="h-5 w-5" />} label={tr({ it: "Sito web", en: "Website", es: "Sitio web", fr: "Site web", de: "Webseite" }, lang)}>
              <a
                href="https://www.ilpozzodeigiganti.it"
                target="_blank"
                rel="noreferrer"
                className="text-sm text-[#4a1c17] font-medium hover:underline"
                data-testid="info-website-link"
              >
                www.ilpozzodeigiganti.it
              </a>
            </InfoRow>
            <InfoRow icon={<Instagram className="h-5 w-5" />} label={tr(T.socials, lang)}>
              <div className="flex gap-3">
                <a href={restaurant?.info?.social?.instagram} target="_blank" rel="noreferrer" className="text-[#7c5a4e] hover:text-[#4a1c17]">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href={restaurant?.info?.social?.facebook} target="_blank" rel="noreferrer" className="text-[#7c5a4e] hover:text-[#4a1c17]">
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
            </InfoRow>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div className="flex gap-3">
      <div className="h-10 w-10 rounded-full bg-[#faf3ee] text-[#4a1c17] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-[#7c5a4e] font-semibold">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}
